import {
  BatchesMap,
  BatchPricesMap,
  BatchTagPrintingData,
  BatchTemplatesMap,
  OfflineBackgroundSyncRegistryAdapter,
  OfflineBatchTagPrintingAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { TAG_PRINT_OFFLINE_QUERY } from '@/components/tag-print-batch-queries';
import { TagPrint_OfflineQuery, TagPrint_OfflineQueryVariables } from '@/types';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';

export const useDownloadTagPrintBatchQueries = () => {
  const getOfflineTagPrintData = useLazyQuery<
    TagPrint_OfflineQuery,
    TagPrint_OfflineQueryVariables
  >(TAG_PRINT_OFFLINE_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const locale = useItemQueryLocale();

  const downloadTagBatchPrintData = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const adapter = OfflineBatchTagPrintingAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );

    const adapterData: BatchTagPrintingData = {
      batches: null,
      prices: null,
      templates: null,
    };

    const variables = {
      locale,
      restaurantUUID,
      skipGroupMemberships: true,
      skipLiveSalt: true,
      skipSaltApi: true,
      supplierUUID,
      useSaltCache: false,
    };

    const { data: offlineTagPrintData } = await getOfflineTagPrintData({
      context: {
        offlineSync: true,
      },
      variables,
    });

    if (offlineTagPrintData.print_tag_batches) {
      const adaptedBatches: BatchesMap = {};
      offlineTagPrintData.print_tag_batches.forEach((batch) => {
        adaptedBatches[batch.uuid] = batch;
      });

      adapterData.batches = adaptedBatches;
    }

    if (offlineTagPrintData.print_tag_template_assignments) {
      const adapterTemplates: BatchTemplatesMap = {};
      const templates = offlineTagPrintData.print_tag_template_assignments.map(
        (assignment) => assignment.print_tag_template,
      );
      templates.forEach((template) => {
        adapterTemplates[template.uuid] = template;
      });

      adapterData.templates = adapterTemplates;
    }

    if (offlineTagPrintData.getVisibleVariantPackPrintingPrices) {
      const adapterPrices: BatchPricesMap = {};
      offlineTagPrintData.getVisibleVariantPackPrintingPrices.variant_pack_prices.forEach(
        (variantPackPrice) => {
          adapterPrices[variantPackPrice.variant_pack_id] = variantPackPrice;
        },
      );

      adapterData.prices = adapterPrices;
    }

    await adapter.save(adapterData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(TAG_PRINT_OFFLINE_QUERY),
        syncType: OfflineSyncType.BatchTagPrinting,
        variables,
      },
    );
  };

  return {
    downloadTagBatchPrintData,
  };
};
